import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mail, Lock, User, Phone, Eye, EyeOff,
  ArrowRight, Globe, Gift, CheckCircle,
  Shield, Zap, ChevronLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';

const P  = '#C29500';
const P2 = '#E8C040';
const C  = '#06B6D4';
const G  = '#E8C040';

const KudiLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="kr-g" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={P2}/>
        <stop offset="100%" stopColor={P}/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#kr-g)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const COUNTRIES = [
  { code:'NG', name:'🇳🇬 Nigeria'       },
  { code:'GH', name:'🇬🇭 Ghana'         },
  { code:'KE', name:'🇰🇪 Kenya'         },
  { code:'ZA', name:'🇿🇦 South Africa'  },
  { code:'UG', name:'🇺🇬 Uganda'        },
  { code:'TZ', name:'🇹🇿 Tanzania'      },
  { code:'SN', name:'🇸🇳 Senegal'       },
  { code:'US', name:'🇺🇸 United States' },
  { code:'GB', name:'🇬🇧 United Kingdom'},
  { code:'CA', name:'🇨🇦 Canada'        },
];

const inputStyle = {
  width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12, padding: '13px 14px 13px 42px', color: '#fff', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s',
};

export default function Register() {
  const [form, setForm]         = useState({ full_name:'', email:'', phone:'', password:'', country:'NG', referral_code:'' });
  const [showPass, setShowPass] = useState(false);
  const [step, setStep]         = useState(1);
  const { register, isLoading } = useAuthStore();
  const navigate                = useNavigate();
  const [searchParams]          = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setForm(f => ({ ...f, referral_code: ref }));
  }, [searchParams]);

  const handleNext = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error('Fill all fields!');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters!');
    const result = await register(form);
    if (result.success) {
      toast.success('Account created! Welcome to Kudibot!');
      navigate('/login');
    } else {
      toast.error(result.error);
    }
  };

  const pwStrength = form.password.length < 6 ? 'weak' : form.password.length < 10 ? 'good' : 'strong';

  return (
    <div style={{ minHeight: '100vh', background: '#060A18', display: 'flex', fontFamily: 'Inter, sans-serif' }}>

      {/* ── LEFT PANEL ── */}
      <div style={{
        display: 'none', flex: '0 0 50%', maxWidth: '50%', position: 'relative',
        overflow: 'hidden', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: 64,
        background: 'linear-gradient(135deg, rgba(194,149,0,0.06) 0%, rgba(6,182,212,0.04) 100%)',
      }} className="lg-panel">

        <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: 420, height: 420,
          background: `radial-gradient(circle, ${P}20 0%, transparent 65%)`, borderRadius: '50%', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', bottom: '-10%', left: '-10%', width: 320, height: 320,
          background: `radial-gradient(circle, ${C}15 0%, transparent 65%)`, borderRadius: '50%', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.02,
          backgroundImage: `linear-gradient(${P} 1px, transparent 1px), linear-gradient(90deg, ${P} 1px, transparent 1px)`,
          backgroundSize: '56px 56px', pointerEvents: 'none' }}/>

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%', maxWidth: 400 }}>

          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 48 }}>
            <div style={{ filter: `drop-shadow(0 0 16px ${P}60)` }}><KudiLogo size={48} /></div>
            <span style={{ fontSize: 32, fontWeight: 900, letterSpacing: -1, color: '#fff' }}>
              Kudi<span style={{ color: P2 }}>bot</span>
            </span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <h2 style={{ fontSize: 34, fontWeight: 900, marginBottom: 12, lineHeight: 1.1, color: '#fff', letterSpacing: -1 }}>
              Your Stablecoin<br/>
              <span style={{
                background: `linear-gradient(135deg, ${P2}, ${C})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>Collection Partner</span>
            </h2>
            <p style={{ color: '#64748B', fontSize: 15, marginBottom: 36, lineHeight: 1.65 }}>
              Join 500+ businesses collecting USDT & USDC and settling to banks across Africa.
            </p>
          </motion.div>

          {/* Benefits */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36, textAlign: 'left' }}>
            {[
              { icon: <CheckCircle size={16}/>, text: 'Free to register, no hidden setup fees', color: G },
              { icon: <CheckCircle size={16}/>, text: 'Accept USDT & USDC via payment links or API', color: G },
              { icon: <CheckCircle size={16}/>, text: 'Bank & MoMo payouts across 180+ countries',   color: G },
              { icon: <CheckCircle size={16}/>, text: 'Funds settle in 1–5 minutes always',           color: G },
              { icon: <Gift size={16}/>,        text: 'Earn 10% referral commission on every plan',   color: P  },
            ].map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ color: f.color, flexShrink: 0 }}>{f.icon}</div>
                <p style={{ color: '#CBD5E1', fontSize: 13, margin: 0 }}>{f.text}</p>
              </motion.div>
            ))}
          </div>

          {/* Plans */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { plan: 'Starter', price: '$29', color: P  },
              { plan: 'Pro',     price: '$59', color: C  },
              { plan: 'Elite',   price: '$99', color: '#F59E0B' },
            ].map((p, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '12px 8px',
                border: `1px solid ${p.color}25`, textAlign: 'center',
              }}>
                <p style={{ fontSize: 11, color: '#475569', margin: '0 0 4px' }}>{p.plan}</p>
                <p style={{ fontWeight: 900, fontSize: 18, color: p.color, margin: '0 0 2px', letterSpacing: -0.5 }}>{p.price}</p>
                <p style={{ fontSize: 10, color: '#334155', margin: 0 }}>/mo</p>
              </div>
            ))}
          </motion.div>

        </div>
      </div>

      {/* ── RIGHT PANEL (Form) ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px' }}>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          style={{ width: '100%', maxWidth: 440 }}>

          {/* Mobile logo */}
          <div className="lg-hide" style={{ textAlign: 'center', marginBottom: 32 }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <KudiLogo size={36} />
              <span style={{ fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: -0.5 }}>
                Kudi<span style={{ color: P2 }}>bot</span>
              </span>
            </Link>
          </div>

          {/* Card */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', borderRadius: 24,
            border: '1px solid rgba(255,255,255,0.08)', padding: '36px 32px',
          }}>

            <div style={{ marginBottom: 20 }}>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 4px', letterSpacing: -0.5 }}>
                Create Account
              </h1>
              <p style={{ color: '#64748B', fontSize: 13, margin: 0 }}>Start collecting stablecoins today</p>
            </div>

            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
              {[{ num:1, label:'Business Info' }, { num:2, label:'Security' }].map((s, i, arr) => (
                <React.Fragment key={s.num}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', fontSize: 12, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: step >= s.num ? `linear-gradient(135deg, ${P}, ${C})` : 'rgba(255,255,255,0.06)',
                      color: step >= s.num ? '#fff' : '#475569',
                      transition: 'all .3s',
                    }}>
                      {step > s.num ? <CheckCircle size={13}/> : s.num}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600,
                      color: step >= s.num ? '#CBD5E1' : '#475569' }}>{s.label}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ flex: 1, height: 2, borderRadius: 2,
                      background: step > s.num ? `linear-gradient(90deg, ${P}, ${C})` : 'rgba(255,255,255,0.06)',
                      transition: 'background .3s' }}/>
                  )}
                </React.Fragment>
              ))}
            </div>

            <AnimatePresence mode="wait">

              {/* Step 1 */}
              {step === 1 && (
                <motion.form key="s1"
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                      letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Business Name</label>
                    <div style={{ position: 'relative' }}>
                      <User size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                      <input type="text" placeholder="e.g. Acme Store" required
                        value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = `${P}60`}
                        onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}/>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                      letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Email Address</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                      <input type="email" placeholder="you@example.com" required
                        value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = `${P}60`}
                        onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}/>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                      letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Country</label>
                    <div style={{ position: 'relative' }}>
                      <Globe size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                      <select value={form.country} onChange={e => setForm({...form, country: e.target.value})}
                        style={{ ...inputStyle, appearance: 'none' }}
                        onFocus={e => e.target.style.borderColor = `${P}60`}
                        onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}>
                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    type="submit"
                    style={{
                      width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg, ${P}, ${C})`,
                      color: '#fff', fontWeight: 800, fontSize: 14,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      marginTop: 4, boxShadow: `0 0 24px ${P}25`,
                    }}>
                    Continue <ArrowRight size={15}/>
                  </motion.button>

                </motion.form>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <motion.form key="s2"
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                      letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Phone Number</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                      <input type="tel" placeholder="+234 800 000 0000" required
                        value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = `${P}60`}
                        onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}/>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                      letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                      <input type={showPass ? 'text' : 'password'} placeholder="Min 6 characters" required
                        value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                        style={{ ...inputStyle, paddingRight: 44 }}
                        onFocus={e => e.target.style.borderColor = `${P}60`}
                        onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}/>
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0 }}>
                        {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                      </button>
                    </div>
                    {form.password && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 4, transition: 'all .3s',
                            width: pwStrength === 'weak' ? '30%' : pwStrength === 'good' ? '65%' : '100%',
                            background: pwStrength === 'weak' ? '#EF4444' : pwStrength === 'good' ? '#F59E0B' : G,
                          }}/>
                        </div>
                        <p style={{ fontSize: 11, marginTop: 4,
                          color: pwStrength === 'weak' ? '#EF4444' : pwStrength === 'good' ? '#F59E0B' : G }}>
                          {pwStrength === 'weak' ? 'Too short' : pwStrength === 'good' ? 'Good' : 'Strong ✓'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
                      color: '#64748B', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                      <Gift size={12} style={{ color: P }}/> Referral Code
                      <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#334155' }}>(Optional)</span>
                    </label>
                    <input type="text" placeholder="Enter referral code"
                      value={form.referral_code} onChange={e => setForm({...form, referral_code: e.target.value})}
                      style={{ ...inputStyle, paddingLeft: 14 }}
                      onFocus={e => e.target.style.borderColor = `${P}60`}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}/>
                    {form.referral_code && (
                      <p style={{ fontSize: 11, color: P2, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle size={10}/> Referral code applied!
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      type="button" onClick={() => setStep(1)}
                      style={{
                        padding: '14px 18px', borderRadius: 12, cursor: 'pointer',
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                        color: '#94A3B8', fontWeight: 600, fontSize: 14,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                      <ChevronLeft size={15}/> Back
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.02, boxShadow: `0 0 32px ${P}40` }}
                      whileTap={{ scale: 0.98 }}
                      type="submit" disabled={isLoading}
                      style={{
                        flex: 1, padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                        background: `linear-gradient(135deg, ${P}, ${C})`,
                        color: '#fff', fontWeight: 800, fontSize: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        boxShadow: `0 0 24px ${P}25`,
                      }}>
                      {isLoading
                        ? <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }}/>
                        : <><span>Create Account</span><ArrowRight size={15}/></>
                      }
                    </motion.button>
                  </div>

                </motion.form>
              )}
            </AnimatePresence>

            <p style={{ textAlign: 'center', color: '#64748B', fontSize: 13, margin: '20px 0 0' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: P2, fontWeight: 700, textDecoration: 'none' }}>
                Sign In →
              </Link>
            </p>

          </div>

          {/* Trust strip */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginTop: 20 }}>
            {[
              [Shield, 'Secure'], [Globe, '180+ Countries'], [Zap, 'Fast Payouts'],
            ].map(([Icon, label], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#334155' }}>
                <Icon size={12} color={P}/> {label}
              </div>
            ))}
          </div>

        </motion.div>
      </div>

      <style>{`
        @media (min-width: 1024px) { .lg-panel { display: flex !important; } .lg-hide { display: none !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder, select::placeholder { color: #334155; }
        select option { background: #0D1117; color: #fff; }
      `}</style>

    </div>
  );
}
